import { useEffect, useState, useMemo } from "react";
import type { ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App, Input, Select, Space, Table, Tag } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { StatusPill, type StatusTone } from "../components/StatusPill";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import VendorDetailsModal from "../components/VendorDetailsModal";
import { TableSkeleton } from "../components/TableSkeleton";
import { EmptyState } from "../components/EmptyState";
import { useApexContext } from "../components/apex/ApexProvider";

interface Vendor {
  id: string;
  profile?: string;
  companyName?: string;
  url?: string;
  primaryPOC?: string;
  primaryPOCEmail?: string;
  capabilityTags?: string[];
  agreementSignedAt?: string;
  agreementRenewalDue?: string;
  agreementFileUrl?: string;
  agreementFileName?: string;
  status?: string;
  sessionId?: string;
  createdAt?: string;
}

const ACCENT = "#CF1F42";

// Map renewal-window remaining days to a StatusPill tone.
function renewalTone(days: number | null): { tone: StatusTone; label: string } {
  if (days === null) return { tone: "neutral", label: "—" };
  if (days < 0) return { tone: "danger", label: "Expired" };
  if (days <= 30) return { tone: "warning", label: `${days}d` };
  return { tone: "success", label: `${days}d` };
}

// Vendor status → tone mapping.
function vendorStatusTone(status?: string): StatusTone {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "info";
    case "ready-to-sign":
      return "warning";
    case "expired":
      return "danger";
    case "inactive":
      return "neutral";
    default:
      return "neutral";
  }
}

export default function Vendors() {
  const { modal, message } = App.useApp();
  const { isAdmin, canWrite } = useAuth();
  const { setPageContext } = useApexContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [renewalFilter, setRenewalFilter] = useState<"all" | "expiring" | "expired">("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const vendorsQuery = query(
      collection(db, "vendorsPublic"),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(vendorsQuery, (snapshot) => {
      const vendorData: Vendor[] = [];
      snapshot.forEach((vendorDoc) => {
        const data = vendorDoc.data() as Vendor;
        // OAIP-68: hide unconfirmed chat drafts (status="in-progress"). Those
        // get persisted on every chat turn so the AI can resume; they're not
        // real vendor records until the user clicks Confirm Profile.
        if ((data.status || "").toLowerCase() === "in-progress") return;
        vendorData.push({ ...data, id: vendorDoc.id });
      });
      setVendors(vendorData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const daysUntilRenewal = (vendor: Vendor): number | null => {
    if (!vendor.agreementRenewalDue) return null;
    const renewalDate = new Date(vendor.agreementRenewalDue);
    const now = new Date();
    return Math.floor(
      (renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
  };

  const isExpiringSoon = (vendor: Vendor): boolean => {
    const days = daysUntilRenewal(vendor);
    return days !== null && days >= 0 && days <= 30;
  };

  const isExpired = (vendor: Vendor): boolean => {
    const days = daysUntilRenewal(vendor);
    return days !== null && days < 0;
  };

  // URL ?id= is the source of truth for the open detail modal — links from
  // briefs, chat, search results, and notifications can deep-link, and the
  // back button works because closing the modal clears the param.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    if (id) {
      const target = vendors.find((v) => v.id === id);
      if (target) {
        setSelectedVendor(target);
        setShowModal(true);
      }
    } else if (showModal) {
      setShowModal(false);
      setSelectedVendor(null);
    }
  }, [location.search, vendors]);

  // Apex: open_entity broadcast routes through the URL so the back button
  // behaves the same way as a clicked link.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { type?: string; id?: string }
        | undefined;
      if (!detail || detail.type !== "vendor" || !detail.id) return;
      navigate(`/vendors?id=${encodeURIComponent(detail.id)}`);
    };
    window.addEventListener("apex:openEntity", handler as EventListener);
    return () => window.removeEventListener("apex:openEntity", handler as EventListener);
  }, [navigate]);

  // Apex: page-context filters.
  useEffect(() => {
    setPageContext({
      filters: {
        searchQuery,
        status: filterStatus,
        renewal: renewalFilter,
      },
    });
  }, [searchQuery, filterStatus, renewalFilter, setPageContext]);

  // Apex: openEntity sync.
  useEffect(() => {
    if (!showModal || !selectedVendor) {
      setPageContext({ openEntity: undefined });
      return;
    }
    setPageContext({
      openEntity: {
        type: "vendor",
        id: selectedVendor.id,
        name: selectedVendor.companyName || selectedVendor.profile?.split("\n")[0] || selectedVendor.id,
        status: selectedVendor.status,
      },
    });
    return () => setPageContext({ openEntity: undefined });
  }, [showModal, selectedVendor, setPageContext]);

  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => {
      if (filterStatus !== "all" && v.status !== filterStatus) {
        return false;
      }

      if (renewalFilter === "expiring" && !isExpiringSoon(v)) {
        return false;
      }
      if (renewalFilter === "expired" && !isExpired(v)) {
        return false;
      }

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const companyName = (v.companyName || v.profile?.split("\n")[0] || "").toLowerCase();
        const profile = (v.profile || "").toLowerCase();
        const tags = (v.capabilityTags || []).join(" ").toLowerCase();

        if (
          !companyName.includes(q) &&
          !profile.includes(q) &&
          !tags.includes(q)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [vendors, filterStatus, renewalFilter, searchQuery]);

  const handleUpdateVendor = async (updates: Partial<Vendor>) => {
    if (!selectedVendor) return;
    const vendorRef = doc(db, "vendorsPublic", selectedVendor.id);
    await updateDoc(vendorRef, updates);
  };

  const handleDeleteVendor = async () => {
    if (!selectedVendor) return;
    modal.confirm({
      title: "Delete vendor",
      content: `Are you sure you want to delete ${selectedVendor.companyName || selectedVendor.id}?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const vendorRef = doc(db, "vendorsPublic", selectedVendor.id);
        await deleteDoc(vendorRef);
        navigate("/vendors");
        message.success("Vendor deleted successfully");
      },
    });
  };

  const handleRowClick = (vendor: Vendor) => {
    navigate(`/vendors?id=${encodeURIComponent(vendor.id)}`);
  };

  const activeVendors = vendors.filter((v) => v.status === "active").length;
  const renewingVendors = vendors.filter((v) => isExpiringSoon(v)).length;
  const expiredVendors = vendors.filter((v) => isExpired(v)).length;

  const columns = [
    {
      title: "Vendor",
      dataIndex: "companyName",
      key: "companyName",
      render: (_: string, vendor: Vendor) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontWeight: 600,
              color: "var(--apex-ink-primary)",
              fontSize: 14,
            }}
          >
            {isExpiringSoon(vendor) && (
              <ExclamationCircleOutlined
                style={{ color: ACCENT, fontSize: 13 }}
                aria-label="Agreement expiring within 30 days"
              />
            )}
            {vendor.companyName || vendor.profile?.split("\n")[0] || "Unknown vendor"}
          </span>
          <span
            style={{
              fontSize: 12.5,
              color: "var(--apex-ink-muted)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 360,
            }}
          >
            {vendor.profile?.split("\n").slice(1).join(" ") || "No description"}
          </span>
        </div>
      ),
    },
    {
      title: "Capabilities",
      dataIndex: "capabilityTags",
      key: "capabilityTags",
      render: (tags: string[] = []) => (
        <Space wrap size={4}>
          {tags.slice(0, 3).map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
          {tags.length > 3 && <Tag>+{tags.length - 3}</Tag>}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string | undefined) => (
        <StatusPill label={status || "pending"} tone={vendorStatusTone(status)} inline />
      ),
    },
    {
      title: "Agreement",
      dataIndex: "agreementSignedAt",
      key: "agreement",
      render: (agreementSignedAt: string | undefined) =>
        agreementSignedAt ? (
          <StatusPill label="Signed" tone="success" inline />
        ) : (
          <StatusPill label="Not signed" tone="neutral" inline />
        ),
    },
    {
      title: "Renewal",
      dataIndex: "agreementRenewalDue",
      key: "renewal",
      className: "apex-col-date",
      render: (_: string | undefined, vendor: Vendor) => {
        const days = daysUntilRenewal(vendor);
        const { tone, label } = renewalTone(days);
        return <StatusPill label={label} tone={tone} inline />;
      },
    },
  ];

  return (
    <div className="apex-page">
      <div className="apex-page-header">
        <div>
          <p className="apex-eyebrow">Network</p>
          <h1 className="apex-page-title" style={{ marginTop: 6 }}>
            Vendors
          </h1>
          <p className="apex-page-subtitle">
            AI Accelerator vendor network with capability profiles and agreement tracking.
          </p>
        </div>
      </div>

      {/* Compact metric strip */}
      <section
        className="apex-surface"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 32,
          padding: "20px 24px",
          flexWrap: "wrap",
        }}
      >
        <div className="apex-metric">
          <span className="apex-metric__label">Total</span>
          <span className="apex-metric__value">{vendors.length}</span>
          <span className="apex-metric__detail">in vendor network</span>
        </div>
        <div className="apex-metric-divider" />
        <div className="apex-metric">
          <span className="apex-metric__label">Active</span>
          <span className="apex-metric__value">{activeVendors}</span>
          <span className="apex-metric__detail">with signed agreements</span>
        </div>
        <div className="apex-metric-divider" />
        <div className="apex-metric">
          <span className="apex-metric__label">Renewing soon</span>
          <span className="apex-metric__value">{renewingVendors}</span>
          <span className="apex-metric__detail">due within 30 days</span>
        </div>
        {expiredVendors > 0 && (
          <>
            <div className="apex-metric-divider" />
            <div className="apex-metric">
              <span className="apex-metric__label">Expired</span>
              <span className="apex-metric__value" style={{ color: "var(--apex-dot-red)" }}>
                {expiredVendors}
              </span>
              <span className="apex-metric__detail">need renewal</span>
            </div>
          </>
        )}
      </section>

      <div>
        <div className="apex-toolbar">
          <Input
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(event.target.value)
            }
            allowClear
            aria-label="Search vendors"
            style={{ minWidth: 240, maxWidth: 320, flex: 1 }}
          />
          <Select
            value={filterStatus}
            onChange={(value: string) => setFilterStatus(value)}
            aria-label="Filter by vendor status"
            options={[
              { value: "all", label: "All status" },
              { value: "pending", label: "Pending" },
              { value: "active", label: "Active" },
              { value: "ready-to-sign", label: "Ready to sign" },
              { value: "inactive", label: "Inactive" },
              { value: "expired", label: "Expired" },
            ]}
            style={{ minWidth: 180 }}
          />
          <Select
            value={renewalFilter}
            onChange={(value: "all" | "expiring" | "expired") => setRenewalFilter(value)}
            aria-label="Filter by renewal date"
            options={[
              { value: "all", label: "All renewals" },
              { value: "expiring", label: "Expiring in 30 days" },
              { value: "expired", label: "Expired" },
            ]}
            style={{ minWidth: 200 }}
          />
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--apex-ink-muted)" }}>
            {filteredVendors.length} of {vendors.length}
          </span>
        </div>

        <div className="apex-table-wrap">
          {loading ? (
            <div style={{ padding: 16 }}>
              <TableSkeleton rows={5} columns={5} />
            </div>
          ) : filteredVendors.length === 0 ? (
            <EmptyState
              description={
                searchQuery || filterStatus !== "all" || renewalFilter !== "all"
                  ? "No vendors match your filters"
                  : "No vendors yet"
              }
            />
          ) : (
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredVendors}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              aria-label="Vendor directory"
              caption="Vendor directory with status, agreements, and renewal dates"
              onRow={(record: Vendor) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: "pointer" },
              })}
            />
          )}
        </div>
      </div>

      {showModal && selectedVendor && (
        <VendorDetailsModal
          vendor={selectedVendor}
          onClose={() => navigate("/vendors")}
          onUpdateVendor={handleUpdateVendor}
          onDeleteVendor={handleDeleteVendor}
          canEdit={canWrite}
          canDelete={isAdmin}
        />
      )}
    </div>
  );
}
